import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import styled from "styled-components";
import Navbar from "../components/webpage/Navbar";
import Footer from "../components/webpage/Footer";

// API base
const API_BASE =
  (process.env.REACT_APP_API_BASE && process.env.REACT_APP_API_BASE.trim()) ||
  "https://server229256.nazwa.pl";

async function fetchJSON(url, options) {
  const res = await fetch(url, options);
  const ct = res.headers.get("content-type") || "";
  if (!ct.includes("application/json")) {
    const text = await res.text();
    console.error("Non-JSON response:", res.status, text);
    throw new Error(`Unexpected response ${res.status} (see console)`);
  }
  const data = await res.json();
  if (!res.ok) {
    throw new Error((data && data.error) || `HTTP ${res.status}`);
  }
  return data;
}

// Fetch user by email (tries /api/users?email=..., falls back to /api/users)
async function fetchUserByEmail(email) {
  const emailLC = (email || "").toLowerCase();

  try {
    const maybeList = await fetchJSON(
      `${API_BASE}/api/users?email=${encodeURIComponent(email)}`
    );
    if (Array.isArray(maybeList)) {
      return (
        maybeList.find(
          (u) => ((u && u.userEmail) || "").toLowerCase() === emailLC
        ) || null
      );
    }
    if (maybeList && ((maybeList.userEmail || "").toLowerCase() === emailLC)) {
      return maybeList;
    }
  } catch {
    // ignore and try fallback
  }

  const users = await fetchJSON(`${API_BASE}/api/users`, { method: "GET" });
  if (!Array.isArray(users)) return null;
  return (
    users.find((u) => ((u && u.userEmail) || "").toLowerCase() === emailLC) ||
    null
  );
}

// Normalize and decide if license is blocking.
function isLicenseActiveBlocking(user) {
  if (!user) return false;
  const typeRaw = user.licenseType;
  const exp = user.licenseExpire;

  const type = (typeRaw ? String(typeRaw) : "").trim().toUpperCase();
  if (!type) return false;

  if (type.startsWith("DEMO")) return false; // allow demos to buy

  if (typeof exp === "string" && exp.toUpperCase() === "NO EXPIRE") return true;

  const d = exp ? new Date(exp) : null;
  if (d && !Number.isNaN(d.getTime()) && d.getTime() > Date.now()) return true;

  return false;
}

function formatPLDate(dt) {
  if (!dt) return null;
  try {
    const d = new Date(dt);
    if (Number.isNaN(d.getTime())) return null;
    return d.toLocaleDateString("pl-PL", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    });
  } catch {
    return null;
  }
}

// Replace first numeric amount inside a price string
function getDiscountedDisplayPrice(displayPrice, percentOff) {
  if (!displayPrice) return null;
  const str = String(displayPrice).replace(",", ".");
  const match = str.match(/([\d]+(?:\.\d+)?)/);

  if (!match) return null;

  const originalNumberStr = match[1];
  const originalNumber = parseFloat(originalNumberStr);
  if (!Number.isFinite(originalNumber)) return null;

  const factor = 1 - percentOff / 100;
  const discountedNumber = originalNumber * factor;

  const discountedStr = discountedNumber.toFixed(2);
  const discountedDisplay = str.replace(originalNumberStr, discountedStr);

  return {
    originalDisplay: displayPrice,
    discountedDisplay,
  };
}

/* ---------------- PROMO HELPERS ---------------- */

function stripDiacritics(s = "") {
  return String(s).normalize("NFD").replace(/[\u0300-\u036f]/g, "");
}

// Detect monthly from various strings like: "month", "monthly", "1 miesiąc", "miesięcznie", etc.
function isMonthlyFromAny(raw = "") {
  const v = stripDiacritics(raw).toLowerCase().trim();
  return v.includes("month") || v.includes("mies"); // miesiac / miesiecznie / miesiac itd.
}

// Detect yearly/annual like: "year", "annual", "rok", "roczny", "12 miesięcy", etc.
function isYearlyFromAny(raw = "") {
  const v = stripDiacritics(raw).toLowerCase().trim();
  return (
    v.includes("year") ||
    v.includes("annual") ||
    v.includes("rok") ||
    v.includes("rocz") ||
    v.includes("12 mies")
  );
}

/** ✅ Frontend-known promo codes (with interval rules) */
function getKnownPromoPercent(codeRaw, ctx = {}) {
  const code = (codeRaw || "").trim().toUpperCase();
  if (!code) return null;

  // Use multiple fields to determine interval, because selectedLicense.interval may be "month"/"year"
  // but sometimes the info is inside value/planParam like "STANDARD – 1 miesiąc"
  const intervalCandidate =
    ctx.interval ?? ctx.planParam ?? ctx.value ?? "";

  if (code === "START20") return 20;
  if (code === "LINKEDIN") return 10;
  if (code === "EUROFISCALIS") return 100;

  // ✅ FREEBIURO: only monthly subscription
  if (code === "FREEBIURO") {
    // If it's explicitly yearly -> invalid
    if (isYearlyFromAny(intervalCandidate)) return null;

    // Valid only if monthly is detected
    return isMonthlyFromAny(intervalCandidate) ? 100 : null;
  }

  return null;
}

const OrderSummaryPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const selectedLicense = location.state && location.state.license;

  const [email, setEmail] = useState("");
  const [discountCode, setDiscountCode] = useState("");

  const [finalPrice, setFinalPrice] = useState(null); // one-time flow
  const [discountApplied, setDiscountApplied] = useState(false);

  const [discountValid, setDiscountValid] = useState(null); // null | true | false
  const [emailValid, setEmailValid] = useState(null);
  const [emailFormatValid, setEmailFormatValid] = useState(true);
  const [isVerifying, setIsVerifying] = useState(false);
  const [isPaying, setIsPaying] = useState(false);

  // UI state for active subscription message
  const [hasActiveSub, setHasActiveSub] = useState(false);
  const [activeSubText, setActiveSubText] = useState("");

  const isSubscription = !!(selectedLicense && selectedLicense.priceId);

  const normalizedCode = useMemo(() => {
    const v = (discountCode || "").trim();
    return v ? v.toUpperCase() : "";
  }, [discountCode]);

  const promoPercent = useMemo(() => {
    return getKnownPromoPercent(normalizedCode, {
      interval: selectedLicense?.interval,
      planParam: selectedLicense?.planParam,
      value: selectedLicense?.value,
    });
  }, [normalizedCode, selectedLicense?.interval, selectedLicense?.planParam, selectedLicense?.value]);

  // Compute price for one-time (no priceId)
  useEffect(() => {
    if (!selectedLicense || selectedLicense.priceId) {
      setFinalPrice(null);
      return;
    }
    const numeric =
      selectedLicense.discounted ||
      parseFloat(
        (selectedLicense.price || "0").replace(" PLN", "").replace(" ", "")
      );
    setFinalPrice(numeric || 0);
    setDiscountApplied(false);
  }, [selectedLicense]);

  // ✅ One-time: apply percent based on known code
  const applyDiscountCode = () => {
    if (!selectedLicense || selectedLicense.priceId) return; // only one-time
    if (!normalizedCode) return;
    if (discountApplied) return;

    const percent = getKnownPromoPercent(normalizedCode, {
      interval: selectedLicense?.interval,
      planParam: selectedLicense?.planParam,
      value: selectedLicense?.value,
    });

    if (!percent) {
      setDiscountValid(false);
      return;
    }

    const discounted = (finalPrice || 0) * (1 - percent / 100);
    setFinalPrice(discounted);
    setDiscountApplied(true);
    setDiscountValid(true);
  };

  // Verify on demand (only when clicking Pay)
  const verifyEmailAndLicenseOnDemand = async () => {
    setEmailValid(null);
    setHasActiveSub(false);
    setActiveSubText("");

    const formatRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const isFormatValid = formatRegex.test(email);
    setEmailFormatValid(isFormatValid);
    if (!isFormatValid) {
      setEmailValid(false);
      return { ok: false, blocking: false, message: null };
    }

    try {
      setIsVerifying(true);

      // 1) Email registered?
      const data = await fetchJSON(`${API_BASE}/api/check-email`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email }),
      });

      const exists = !!(data && data.exists);
      setEmailValid(exists);
      if (!exists) {
        return { ok: false, blocking: false, message: null };
      }

      // 2) Fetch user & compute blocking
      const user = await fetchUserByEmail(email);
      const blocking = isLicenseActiveBlocking(user);

      if (blocking) {
        const expTxt =
          (typeof user.licenseExpire === "string" &&
            user.licenseExpire.toUpperCase() === "NO EXPIRE") ||
          !user.licenseExpire
            ? "bezterminowa"
            : `do ${formatPLDate(user.licenseExpire)}`;

        const msg = `Masz już aktywną subskrypcję (${user.licenseType || "brak typu"}, ${expTxt}). Jeśli chcesz anulować lub zmienić swoją subskrypcję, skontaktuj się z nami: biuro@analizyiraporty.pl`;

        setHasActiveSub(true);
        setActiveSubText(msg);

        return { ok: true, blocking: true, message: msg };
      }

      setHasActiveSub(false);
      setActiveSubText("");
      return { ok: true, blocking: false, message: null };
    } catch (error) {
      console.error("❌ Error verifying email/subscription:", error);
      setEmailValid(false);
      setHasActiveSub(false);
      setActiveSubText("");
      return { ok: false, blocking: false, message: null };
    } finally {
      setIsVerifying(false);
    }
  };

  const handlePayment = async () => {
    // Run checks now
    const result = await verifyEmailAndLicenseOnDemand();
    if (!result.ok) return;

    if (result.blocking) {
      setHasActiveSub(true);
      if (result.message) setActiveSubText(result.message);
      return;
    }

    setIsPaying(true);
    try {
      // ✅ For subscription: only send promoCode if it's one of the known codes AND valid for interval
      const promoCodeToSend =
        isSubscription && promoPercent ? normalizedCode : null;

      const data = await fetchJSON(`${API_BASE}/api/create-checkout-session`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          priceId: selectedLicense.priceId, // subscription (if present)
          email,
          promoCode: promoCodeToSend,
          metadata: {
            tier: selectedLicense.tier,
            planParam: selectedLicense.planParam,
            interval: selectedLicense.interval,
            discountCode: normalizedCode || "",
            orderPrice: selectedLicense.orderPrice || "",
            license: selectedLicense.value,
          },
          successUrl: `${window.location.origin}/success`,
          cancelUrl: `${window.location.origin}/cancel`,
        }),
      });

      localStorage.setItem(
        "paymentInfo",
        JSON.stringify({
          email,
          discountCode: normalizedCode,
          license: selectedLicense.value,
          price: selectedLicense.displayPrice,
          timestamp: new Date().toISOString(),
        })
      );

      window.location.href = data.url;
    } catch (e) {
      console.error("❌ Payment error:", e);
    } finally {
      setIsPaying(false);
    }
  };

  return (
    <>
      <Navbar />
      <PageBackground>
        <Container>
          <h2>Podsumowanie zamówienia</h2>

          {!selectedLicense ? (
            <p style={{ padding: "2rem" }}>
              Nie wybrano licencji. Wróć do cennika.
            </p>
          ) : (
            <Card>
              <Section>
                <h3>1. Konto użytkownika</h3>
                <Input
                  type="email"
                  placeholder="Podaj swój email, jeśli już masz konto"
                  value={email}
                  onChange={(e) => {
                    setEmail(e.target.value);
                    setEmailValid(null);
                    setEmailFormatValid(true);
                    setHasActiveSub(false);
                    setActiveSubText("");
                  }}
                  disabled={isVerifying || isPaying}
                />

                {!emailFormatValid && (
                  <Note style={{ color: "red" }}>
                    Nieprawidłowy format adresu email.
                  </Note>
                )}
                {emailFormatValid && emailValid === false && (
                  <Note style={{ color: "red" }}>Email nie jest zarejestrowany.</Note>
                )}
                {emailFormatValid && emailValid === true && !hasActiveSub && (
                  <Note style={{ color: "green" }}>✅ Email jest poprawny!</Note>
                )}
                {hasActiveSub && (
                  <Note
                    role="alert"
                    aria-live="polite"
                    style={{ color: "#b91c1c", fontWeight: 600 }}
                  >
                    {activeSubText}
                  </Note>
                )}

                <Note>
                  Nie masz konta{" "}
                  <RegisterLink onClick={() => navigate("/register")}>
                    Zarejestruj się tutaj
                  </RegisterLink>
                </Note>
              </Section>

              <Section>
                <h3>2. Kod rabatowy (opcjonalny)</h3>
                <Input
                  type="text"
                  placeholder="Np. KODE"
                  value={discountCode}
                  onChange={(e) => {
                    const value = e.target.value;
                    setDiscountCode(value);

                    const n = value.trim().toUpperCase();
                    if (!n) {
                      setDiscountValid(null);
                      return;
                    }

                    const pct = getKnownPromoPercent(n, {
                      interval: selectedLicense?.interval,
                      planParam: selectedLicense?.planParam,
                      value: selectedLicense?.value,
                    });

                    if (pct) setDiscountValid(true);
                    else setDiscountValid(false);
                  }}
                  disabled={isVerifying || isPaying}
                />

                {/* One-time payment – apply button */}
                {!isSubscription && (
                  <>
                    <ApplyButton
                      onClick={applyDiscountCode}
                      disabled={
                        isVerifying ||
                        isPaying ||
                        discountApplied ||
                        !discountCode.trim()
                      }
                    >
                      Zastosuj kod
                    </ApplyButton>

                    {discountValid === true && discountApplied && promoPercent && (
                      <DiscountNote>
                        ✅ Zastosowano {promoPercent}% zniżki z kodu{" "}
                        <strong>{normalizedCode}</strong>!
                      </DiscountNote>
                    )}

                    {discountValid === false && discountCode.trim() && (
                      <Note style={{ marginTop: 8, color: "red" }}>
                        ❌ Nieprawidłowy kod rabatowy.
                      </Note>
                    )}

                    <Note style={{ marginTop: 8 }}>
                      Kod rabatowy obniża cenę przy płatności jednorazowej.
                    </Note>
                  </>
                )}

                {/* Subscription – Stripe info + preview discount */}
                {isSubscription && (
                  <>
                    <Note>
                      Kod rabatowy zostanie zastosowany na stronie płatności
                      Stripe. Widoczna powyżej cena może się nie zmienić, ale
                      Stripe naliczy zniżkę przy pierwszej płatności.
                    </Note>

                    {discountValid === true && promoPercent && (
                      <DiscountNote style={{ marginTop: 4 }}>
                        ✅ Kod jest poprawny – naliczymy {promoPercent}% zniżki przy pierwszej płatności.
                      </DiscountNote>
                    )}

                    {discountValid === false && discountCode.trim() && (
                      <Note style={{ marginTop: 4, color: "red" }}>
                        ❌ Nieprawidłowy kod rabatowy.
                      </Note>
                    )}
                  </>
                )}
              </Section>

              <SummaryBox>
                <LicenseLine>
                  <span>Licencja:</span>
                  <strong>
                    {(selectedLicense.value || "").split(" - ").join(" – ")}
                  </strong>
                </LicenseLine>

                <p>
                  Do zapłaty:{" "}
                  {isSubscription ? (
                    (() => {
                      // If subscription and known code is valid → show preview discount
                      if (
                        discountValid === true &&
                        promoPercent &&
                        selectedLicense?.displayPrice
                      ) {
                        const result = getDiscountedDisplayPrice(
                          selectedLicense.displayPrice,
                          promoPercent
                        );

                        if (result) {
                          return (
                            <>
                              <OriginalPrice>{result.originalDisplay}</OriginalPrice>{" "}
                              <Price>{result.discountedDisplay}</Price>
                            </>
                          );
                        }
                      }

                      return <Price>{selectedLicense.displayPrice}</Price>;
                    })()
                  ) : discountApplied ? (
                    <>
                      {promoPercent ? (
                        <OriginalPrice>
                          {(Number(finalPrice) / (1 - promoPercent / 100)).toFixed(2)} PLN
                        </OriginalPrice>
                      ) : null}{" "}
                      <Price>
                        {finalPrice != null ? finalPrice.toFixed(2) : "0.00"} PLN
                      </Price>
                    </>
                  ) : (
                    <Price>
                      {finalPrice != null ? finalPrice.toFixed(2) : "0.00"} PLN
                    </Price>
                  )}
                </p>

                <PayButton
                  disabled={isVerifying || isPaying || hasActiveSub}
                  onClick={handlePayment}
                  style={{
                    opacity: isVerifying || isPaying || hasActiveSub ? 0.6 : 1,
                    cursor:
                      isVerifying || isPaying || hasActiveSub
                        ? "not-allowed"
                        : "pointer",
                  }}
                  aria-disabled={isVerifying || isPaying || hasActiveSub}
                >
                  {isVerifying
                    ? "Sprawdzanie email..."
                    : isPaying
                    ? "Przetwarzanie..."
                    : "Przejdź do płatności"}
                </PayButton>

                {isSubscription && (
                  <CancellationNote>
                    Subskrypcję możesz anulować w dowolnym momencie – po
                    rezygnacji zachowasz dostęp do systemu aż do ostatniego dnia
                    opłaconego okresu rozliczeniowego.
                  </CancellationNote>
                )}
              </SummaryBox>
            </Card>
          )}
        </Container>
      </PageBackground>
      <Footer />
    </>
  );
};

export default OrderSummaryPage;

/* --- styles --- */
const PageBackground = styled.div`
  background: url("/back.jpg") no-repeat center center fixed;
  background-size: cover;
  min-height: 100vh;
  padding: 3rem 1rem;
  display: flex;
  justify-content: center;
  align-items: flex-start;
  backdrop-filter: blur(10px);
  background-color: rgba(255, 255, 255, 0.8);
`;
const Container = styled.div`
  max-width: 800px;
  width: 100%;
`;
const OriginalPrice = styled.span`
  text-decoration: line-through;
  color: #9ca3af;
  font-weight: 500;
  margin-right: 0.5rem;
`;
const LicenseLine = styled.div`
  background: rgba(243, 244, 246, 0.8);
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  padding: 0.8rem 1.2rem;
  font-size: 1.05rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
  color: #374151;
  span {
    font-weight: 500;
    color: #6b7280;
  }
  strong {
    font-weight: 700;
    color: #1f2937;
  }
`;
const Card = styled.div`
  background: rgba(255, 255, 255, 0.85);
  border-radius: 20px;
  padding: 2.5rem;
  box-shadow: 0 12px 30px rgba(0, 0, 0, 0.1);
  backdrop-filter: blur(12px);
  animation: fadeInUp 0.6s ease;
  @keyframes fadeInUp {
    from {
      opacity: 0;
      transform: translateY(30px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
  h2 {
    text-align: center;
    font-size: 1.8rem;
    margin-bottom: 2rem;
    color: #1f2937;
  }
`;
const Section = styled.div`
  margin-bottom: 2.5rem;
  h3 {
    font-size: 1.2rem;
    margin-bottom: 0.8rem;
    color: #1f2937;
  }
`;
const Input = styled.input`
  width: 100%;
  padding: 0.75rem 1rem;
  font-size: 1rem;
  border: 1px solid #d1d5db;
  border-radius: 10px;
  margin-bottom: 0.8rem;
  background: white;
  &:focus {
    border-color: #3b82f6;
    outline: none;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.2);
  }
`;
const ApplyButton = styled.button`
  padding: 0.6rem 1.5rem;
  background: linear-gradient(to right, #fde68a, #facc15);
  border: none;
  border-radius: 12px;
  font-weight: 600;
  margin-top: 0.3rem;
  cursor: pointer;
  &:hover {
    background: linear-gradient(to right, #facc15, #f59e0b);
  }
  &:disabled {
    filter: grayscale(0.3);
  }
`;
const PayButton = styled.button`
  margin-top: 1.5rem;
  background: linear-gradient(to right, #10b981, #34d399);
  color: white;
  font-weight: bold;
  padding: 0.75rem 2.5rem;
  font-size: 1.2rem;
  border: none;
  border-radius: 50px;
  cursor: pointer;
  transition: 0.3s;
  &:hover {
    background: linear-gradient(to right, #059669, #047857);
    transform: scale(1.05);
  }
  &:disabled {
    transform: none;
  }
`;
const SummaryBox = styled.div`
  padding: 1.8rem;
  margin-top: 2rem;
  font-size: 1.1rem;
  text-align: center;
  p {
    margin: 0.5rem 0;
  }
`;
const Price = styled.span`
  font-size: 1.5rem;
  font-weight: bold;
  color: #16a34a;
`;
const DiscountNote = styled.div`
  color: #16a34a;
  font-size: 0.95rem;
  margin-top: 0.5rem;
`;
const Note = styled.p`
  font-size: 0.95rem;
  color: #6b7280;
`;
const RegisterLink = styled.span`
  color: #3b82f6;
  text-decoration: underline;
  cursor: pointer;
  font-weight: 500;
  &:hover {
    color: #2563eb;
  }
`;
const CancellationNote = styled.p`
  margin-top: 1.25rem;
  font-size: 0.9rem;
  color: #4b5563;
  background: rgba(240, 253, 250, 0.9);
  border-radius: 999px;
  padding: 0.6rem 1.4rem;
  border: 1px dashed #6ee7b7;
`;